import { useSelector } from 'react-redux';
import { 
  isClubManager, 
  getUserClubIds, 
  getUserTournamentIds,
  hasClubAccess,
  hasTournamentAccess 
} from '../utils/roleBasedAccess';

export const useRoleBasedData = () => {
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  const userData = {
    ...user,
    subRole: role,
    permissions: permissions
  };

  const isClubManagerUser = isClubManager(userData);
  const clubIds = getUserClubIds(userData);
  const tournamentIds = getUserTournamentIds(userData);

  // Filter data based on user's club access
  const filterDataByClubAccess = <T extends { clubId?: string; club?: { _id?: string } }>(
    data: T[]
  ): T[] => {
    if (!isClubManagerUser) {
      return data; // Admin users see all data
    }

    return data.filter(item => {
      const itemClubId = item.clubId || item.club?._id;
      return itemClubId && hasClubAccess(userData, itemClubId);
    });
  };

  // Filter data based on user's tournament access
  const filterDataByTournamentAccess = <T extends { tournamentId?: string; tournament?: { _id?: string } }>(
    data: T[]
  ): T[] => {
    if (!isClubManagerUser) {
      return data; // Admin users see all data
    }

    return data.filter(item => {
      const itemTournamentId = item.tournamentId || item.tournament?._id;
      return itemTournamentId && hasTournamentAccess(userData, itemTournamentId);
    });
  };

  // Get API query parameters for club filtering
  const getClubFilterParams = () => {
    if (!isClubManagerUser) {
      return {}; // Admin users don't need filtering
    }

    return {
      clubIds: clubIds.join(',')
    };
  };

  // Get API query parameters for tournament filtering
  const getTournamentFilterParams = () => {
    if (!isClubManagerUser) {
      return {}; // Admin users don't need filtering
    }

    return {
      tournamentIds: tournamentIds.join(',')
    };
  };

  // Check if user can access a specific resource
  const canAccessResource = (resourceType: string, resourceId?: string) => {
    switch (resourceType) {
      case 'club':
        return !isClubManagerUser || (resourceId && hasClubAccess(userData, resourceId));
      case 'tournament':
        return !isClubManagerUser || (resourceId && hasTournamentAccess(userData, resourceId));
      default:
        return true;
    }
  };

  // Get user's accessible club IDs for API calls
  const getAccessibleClubIds = () => {
    if (!isClubManagerUser) {
      return []; // Admin users don't need filtering
    }
    return clubIds;
  };

  // Get user's accessible tournament IDs for API calls
  const getAccessibleTournamentIds = () => {
    if (!isClubManagerUser) {
      return []; // Admin users don't need filtering
    }
    return tournamentIds;
  };

  return {
    userData,
    isClubManagerUser,
    clubIds,
    tournamentIds,
    filterDataByClubAccess,
    filterDataByTournamentAccess,
    getClubFilterParams,
    getTournamentFilterParams,
    canAccessResource,
    getAccessibleClubIds,
    getAccessibleTournamentIds
  };
}; 